import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

export type TextAnimationStyle = "rolling" | "typewriter" | "fade" | "slide-up";

interface HomeRotatingTextProps {
  words?: string[];
  animationStyle?: TextAnimationStyle;
}

const DEFAULT_TOPICS = [
  "AI Agents",
  "Feedback",
  "RAG",
  "Focus",
  "Prompts",
  "Habits",
];

/**
 * Rotating text animation for hero section.
 * Supports multiple animation styles configurable from admin.
 */
export function HomeRotatingText({ words, animationStyle = "rolling" }: HomeRotatingTextProps) {
  const topics = words && words.length > 0 ? words : DEFAULT_TOPICS;

  if (animationStyle === "typewriter") {
    return <TypewriterText topics={topics} />;
  }

  if (animationStyle === "fade") {
    return <FadeText topics={topics} />;
  }

  if (animationStyle === "slide-up") {
    return <SlideUpText topics={topics} />;
  }

  return <RollingText topics={topics} />;
}

/* ── Rolling (original) ── */
function RollingText({ topics }: { topics: string[] }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setHasMounted(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!hasMounted) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % topics.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [hasMounted, topics.length]);

  if (!hasMounted) {
    return <span className="inline-block">{topics[0]}</span>;
  }

  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={`${currentIndex}-${topics[currentIndex]}`}
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -20, opacity: 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="inline-block"
      >
        {topics[currentIndex]}
      </motion.span>
    </AnimatePresence>
  );
}

/* ── Typewriter with backspace ── */
function TypewriterText({ topics }: { topics: string[] }) {
  const [displayed, setDisplayed] = useState("");
  const [wordIndex, setWordIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const tick = useCallback(() => {
    const currentWord = topics[wordIndex];

    if (!isDeleting) {
      // Typing forward
      const next = currentWord.slice(0, displayed.length + 1);
      setDisplayed(next);

      if (next === currentWord) {
        // Pause before deleting
        timeoutRef.current = setTimeout(() => setIsDeleting(true), 1800);
        return;
      }
      timeoutRef.current = setTimeout(tick, 80 + Math.random() * 40);
    } else {
      // Deleting
      const next = currentWord.slice(0, displayed.length - 1);
      setDisplayed(next);

      if (next === "") {
        setIsDeleting(false);
        setWordIndex((prev) => (prev + 1) % topics.length);
        timeoutRef.current = setTimeout(tick, 300);
        return;
      }
      timeoutRef.current = setTimeout(tick, 40 + Math.random() * 20);
    }
  }, [displayed, isDeleting, wordIndex, topics]);

  useEffect(() => {
    if (!hasMounted) return;
    timeoutRef.current = setTimeout(tick, 100);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [tick, hasMounted]);

  if (!hasMounted) {
    return <span className="inline-block">{topics[0]}</span>;
  }

  return (
    <span className="inline-block">
      {displayed}
      <span className="animate-pulse ml-[1px] border-r-2 border-primary">&nbsp;</span>
    </span>
  );
}

/* ── Fade ── */
function FadeText({ topics }: { topics: string[] }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setHasMounted(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!hasMounted) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % topics.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [hasMounted, topics.length]);

  if (!hasMounted) {
    return <span className="inline-block">{topics[0]}</span>;
  }

  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={currentIndex}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.6, ease: "easeInOut" }}
        className="inline-block"
      >
        {topics[currentIndex]}
      </motion.span>
    </AnimatePresence>
  );
}

/* ── Slide-Up ── */
function SlideUpText({ topics }: { topics: string[] }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setHasMounted(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!hasMounted) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % topics.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [hasMounted, topics.length]);

  if (!hasMounted) {
    return <span className="inline-block">{topics[0]}</span>;
  }

  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={currentIndex}
        initial={{ y: 40, opacity: 0, scale: 0.95 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: -40, opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="inline-block"
      >
        {topics[currentIndex]}
      </motion.span>
    </AnimatePresence>
  );
}
